import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { excludedIdfirmsSearchSchema } from "@/features/raporty-dashboard/lib/dashboard-pharmacies";
import { prefetchDashboardInitial } from "@/features/raporty-dashboard/lib/dashboard-prefetch";
import {
	defaultDashboardRange,
	NAMED_PRESETS,
} from "@/features/raporty-dashboard/lib/date-presets";
import { DashboardAnalitycznyPage } from "@/features/raporty-dashboard/page";
import { ensureElementVisible } from "@/lib/route-guards";

const isoDate = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/)
	.optional();

const searchSchema = z.object({
	scope: z.string().optional(),
	excludedIdfirms: excludedIdfirmsSearchSchema,
	dateFrom: isoDate,
	dateTo: isoDate,
	/**
	 * Preset, którym ustawiono zakres. Brak = własny zakres. Daty zostają
	 * autorytatywne (query keys i API czytają TYLKO dateFrom/dateTo); preset
	 * rozstrzyga podświetlenie i etykietę „Dane za” tam, gdzie dwa presety
	 * dają ten sam zakres (styczeń: bieżący miesiąc = YTD).
	 */
	preset: z.enum(NAMED_PRESETS).optional().catch(undefined),
	category: z.string().optional(),
	kosmetyk: z.boolean().optional(),
	/** Segment paska „Natubay” (producent z kartoteki BLOZ); flaga jak `kosmetyk`. */
	natubay: z.boolean().optional(),
	rodzaj: z.enum(["ALL", "OTC", "RX", "RX100", "BD"]).optional(),
	/** CSV kodów kategorii sekcji Kategorie/Towary (multiselect). */
	categories: z
		.string()
		.regex(/^[0-9]+(,[0-9]+)*$/)
		.optional(),
	/** Miara rankingu kategorii (domyślnie sprzedaż netto). */
	rankingMeasure: z.enum(["net", "qty", "margin"]).optional(),
	/** Główna miara listy SKU w sekcji Kategorie/Towary (brak = sprzedaż). */
	skuSources: z.string().optional().catch(undefined),
	skuContract: z.boolean().optional().catch(undefined),
	skuMeasure: z.enum(["sales", "margin", "stock"]).optional().catch(undefined),
	/**
	 * Ścieżka rozwiniętej gałęzi hierarchii kategorii: kody rodzic → dziecko
	 * rozdzielone kropką (`08.0801`). Jedna aktywna gałąź naraz; walidację,
	 * czy ścieżka istnieje w danych, robi karta (przycina od pierwszego
	 * niedopasowania). `.catch(undefined)` chroni stary/zepsuty deep link.
	 */
	branch: z
		.string()
		.regex(/^[0-9]+(\.[0-9]+)*$/)
		.optional()
		.catch(undefined),
	/**
	 * Kategorie otwarte w szczególe: CSV DOKŁADNYCH kodów primary + jawny
	 * token `__none` („Bez kategorii”). To selekcja wierszy rankingu, nie
	 * drugi filtr populacji — populację nadal wyznacza `categories`.
	 * `.catch(undefined)` chroni deep link ze starego/zepsutego URL-a przed
	 * wywaleniem całej trasy ZodError-em.
	 */
	detail: z
		.string()
		.regex(/^(?:__none|[0-9]+)(?:,(?:__none|[0-9]+))*$/)
		.optional()
		.catch(undefined),
	/** Ziarno wykresu szczegółu: brak = dziennie, `month` = widok roczny. */
	detailGrain: z.enum(["day", "month"]).optional().catch(undefined),
	/**
	 * „Porównanie w ramach zakresu”: obecność = sekcja rozwinięta; `auto` =
	 * wymiar domyślny serwera, inaczej jawny wymiar. Brak = zwinięta.
	 */
	breakdown: z.enum(["auto", "kr", "spolka", "region", "apteka"]).optional().catch(undefined),
	/** „Marża wg widełek zakupu”: `true` = sekcja rozwinięta (F5 i deep link). */
	widelki: z.boolean().optional().catch(undefined),
	/** Horyzont „Ryzyka krótkiej daty” (dni); brak = 180. */
	expiryHorizon: z
		.union([z.literal(90), z.literal(180), z.literal(365)])
		.optional()
		.catch(undefined),
});

export type DashboardAnalitycznySearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_auth/raporty/dashboard-analityczny")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.raporty.dashboard-analityczny",
			location.pathname,
		);
	},
	loaderDeps: ({ search }) => {
		// Domyślny koniec zakresu = ostatni kompletny dzień rodzin MV (nie
		// „dziś” — dateTo > dataAsOf dostaje z API 422).
		const defaults = defaultDashboardRange();
		return {
			scope: search.scope,
			excludedIdfirms: search.excludedIdfirms,
			dateFrom: search.dateFrom ?? defaults.dateFrom,
			dateTo: search.dateTo ?? defaults.dateTo,
			category: search.category,
			kosmetyk: search.kosmetyk,
			natubay: search.natubay,
			rodzaj: search.rodzaj,
			categories: search.categories,
			detail: search.detail,
		};
	},
	// Initial graph = tylko widgety renderowane od razu, ze scope rozstrzyganym
	// tak samo jak na stronie (identyczny query key => jeden request).
	loader: ({ context: { queryClient }, deps }) => prefetchDashboardInitial(queryClient, deps),
	component: DashboardAnalitycznyPage,
	validateSearch: (search: Record<string, unknown>): DashboardAnalitycznySearch =>
		searchSchema.parse(search),
});
